import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useAuth, api as axios } from '../Contexts/AuthContext';
import { API_BASE_URL } from '../config';
import '../styles/clientInvoice.css'; // Reuse the excellent styles

export default function OrganizationDetails() {
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [metaVerifyToken, setMetaVerifyToken] = useState('');
  const [defaultWebhook, setDefaultWebhook] = useState(null);
  const [bufferLeads, setBufferLeads] = useState([]);
  const [bufferLoading, setBufferLoading] = useState(false);
  const [editLead, setEditLead] = useState(null);

  useEffect(() => {
    fetchOrg();
    fetchWebhook();
    fetchBufferLeads();
  }, []);

  const fetchWebhook = async () => {
    try {
      const res = await axios.get('crm/webhooks/settings/');
      if (res.data && res.data.length > 0) {
        setDefaultWebhook(res.data[0]);
      } else {
        // Auto create a default one
        const createRes = await axios.post('crm/webhooks/settings/', {
          name: "Default Inbound Webhook"
        });
        setDefaultWebhook(createRes.data);
      }
    } catch (err) {
      console.error("Error fetching webhook settings", err);
    }
  };

  const fetchBufferLeads = async () => {
    try {
      setBufferLoading(true);
      const res = await axios.get('crm/webhooks/buffer/?status=pending');
      setBufferLeads(res.data || []);
    } catch (err) {
      console.error("Error fetching webhook buffer leads", err);
    } finally {
      setBufferLoading(false);
    }
  };

  const handleTransfer = async (id, mode) => {
    try {
      const res = await axios.post(`crm/webhooks/buffer/${id}/transfer/`, { mode });
      toast.success(res.data.message || "Lead transferred successfully");
      fetchBufferLeads();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to transfer lead");
    }
  };

  const handleIgnore = async (id) => {
    if (!window.confirm("Are you sure you want to ignore and discard this buffered lead?")) return;
    try {
      await axios.post(`crm/webhooks/buffer/${id}/ignore/`);
      toast.success("Lead ignored successfully");
      fetchBufferLeads();
    } catch (err) {
      toast.error("Failed to ignore lead");
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editLead) return;
    try {
      await axios.patch(`crm/webhooks/buffer/${editLead.id}/`, editLead);
      toast.success("Buffered lead details updated!");
      setEditLead(null);
      fetchBufferLeads();
    } catch (err) {
      toast.error("Failed to update lead details");
    }
  };

  const handleRegenerateWebhookToken = async () => {
    if (!window.confirm("Are you sure you want to regenerate the webhook token? External integrations using the old URL will stop working.")) return;
    try {
      await axios.delete(`crm/webhooks/settings/${defaultWebhook.id}/`);
      toast.success("Webhook token regenerated!");
      fetchWebhook();
    } catch (err) {
      toast.error("Failed to regenerate token");
    }
  };

  const fetchOrg = async () => {
    try {
      const res = await axios.get('organizations/me/');
      setOrg(res.data);
      
      try {
        const metaRes = await axios.get('meta-ads/webhook-info/');
        setMetaVerifyToken(metaRes.data.verify_token || '');
      } catch (err) {
        console.warn("Could not fetch Meta Ads webhook verify token", err);
      }
    } catch (err) {
      console.error("Error fetching org", err);
      if (err.response?.status === 404) {
        setMessage({ 
          text: 'No organization found. Please contact support.', 
          type: 'danger' 
        });
      } else if (err.response?.status === 401) {
        setMessage({ text: 'Session expired. Please log in again.', type: 'danger' });
      } else {
        setMessage({ text: 'Unable to load profile. Please try again later.', type: 'danger' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setOrg(prev => ({ ...prev, [name]: value }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const { setOnboardingComplete, onboardingComplete } = useAuth();

  const saveOrg = async () => {
    // Basic validation for "all details"
    const requiredFields = ['company_code', 'contact_person', 'billing_email', 'phone', 'gst_number', 'pan', 'address', 'city', 'state', 'pincode'];
    const missing = requiredFields.filter(f => !org[f] || org[f].trim() === '');
    
    if (missing.length > 0) {
      setMessage({ text: 'Please fill all fields to complete organization setup.', type: 'danger' });
      return;
    }

    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const payload = { ...org, onboarding_complete: true };
      await axios.patch('organizations/me/', payload);
      setMessage({ text: 'Organization details updated successfully! Setup complete.', type: 'success' });
      setOnboardingComplete(true);
      localStorage.setItem("onboardingComplete", "true");
      setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    } catch (err) {
      let errorMsg = 'Error saving details. Please check your inputs.';
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMsg = err.response.data;
        } else if (err.response.data.detail) {
          errorMsg = err.response.data.detail;
        } else if (typeof err.response.data === 'object') {
          const firstKey = Object.keys(err.response.data)[0];
          const firstErr = err.response.data[firstKey];
          const errMsgText = Array.isArray(firstErr) ? firstErr[0] : firstErr;
          const formattedKey = firstKey.charAt(0).toUpperCase() + firstKey.slice(1).replace(/_/g, ' ');
          errorMsg = `${formattedKey}: ${errMsgText}`;
        }
      }
      setMessage({ text: errorMsg, type: 'danger' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="ci-root" style={{ padding: 40 }}>Loading Profile...</div>;

  if (!org) {
    return (
      <div className="ci-root" style={{ padding: 40 }}>
        <div className="ci-review-box" style={{ background: '#fef2f2', borderColor: '#e05c5c' }}>
          <p style={{ color: '#c0392b', fontWeight: 600 }}>
             ⚠ {message.text || 'Unable to load organization details.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="ci-root">
      <div className="ci-header">
        <div className="ci-header-left">
          <span className="ci-eyebrow">Settings &middot; Profile</span>
          <h1>Organization Details</h1>
          <p>Manage your entity's billing information and statutory records.</p>
        </div>
      </div>

      {!onboardingComplete && (
        <div className="ci-review-box" style={{ background: '#fff8e6', borderColor: '#e6a817', marginBottom: 24, padding: '16px 20px', maxWidth: '900px' }}>
          <p style={{ color: '#c47f0a', fontWeight: 600, fontSize: '14px', margin: 0 }}>
            🚀 <span style={{ marginLeft: 8 }}>Initial Setup Required:</span> Please fill in all the details below to activate your organization workspace and enable team management.
          </p>
        </div>
      )}

      <div className="ci-card" style={{ maxWidth: "900px" }}>
        <h3 className="ci-card-title">Billing Identity</h3>
        <p className="ci-card-sub">This information will appear on your tax invoices.</p>

        {message.text && (
          <div className={`ci-review-box`} style={{ 
            background: message.type === 'success' ? '#edf7ee' : '#fef2f2',
            borderColor: message.type === 'success' ? '#36a849' : '#e05c5c',
            marginBottom: 20
          }}>
            <p style={{ color: message.type === 'success' ? '#2a7a38' : '#c0392b', fontWeight: 600 }}>
               {message.type === 'success' ? '✓ ' : '⚠ '}{message.text}
            </p>
          </div>
        )}

        <div className="ci-form-grid2">
          <div className="ci-field">
            <label className="ci-label">Organization name</label>
            <input name="name" value={org.name || ""} onChange={handleChange} className="ci-input" />
          </div>

          <div className="ci-field">
            <label className="ci-label">Company Code (e.g., ITK)</label>
            <input name="company_code" value={org.company_code || ""} onChange={handleChange} placeholder="3-4 chars for Employee ID prefix" className="ci-input" maxLength="10" />
          </div>

          <div className="ci-field">
            <label className="ci-label">Primary Contact Person</label>
            <input name="contact_person" value={org.contact_person || ""} onChange={handleChange} placeholder="e.g., John Doe" className="ci-input" />
          </div>

          <div className="ci-field">
            <label className="ci-label">Billing Email</label>
            <input name="billing_email" type="email" value={org.billing_email || ""} onChange={handleChange} placeholder="billing@org.com" className="ci-input" />
          </div>

          <div className="ci-field">
            <label className="ci-label">Phone Number</label>
            <input name="phone" value={org.phone || ""} onChange={handleChange} placeholder="+91 98765 43210" className="ci-input" />
          </div>

          <div className="ci-field">
            <label className="ci-label">Industry</label>
            <input value={org.industry_name} disabled className="ci-input" style={{ background: "#f0f0f0" }} />
          </div>

          <div className="ci-field">
            <label className="ci-label">GSTIN (Tax ID)</label>
            <input name="gst_number" value={org.gst_number || ""} onChange={handleChange} placeholder="09XXXXX0000X1ZX" className="ci-input" />
          </div>

          <div className="ci-field">
            <label className="ci-label">PAN Number</label>
            <input name="pan" value={org.pan || ""} onChange={handleChange} placeholder="ABCDE1234F" className="ci-input" />
          </div>

          <div className="ci-field span2">
            <label className="ci-label">Billing Address</label>
            <input name="address" value={org.address || ""} onChange={handleChange} placeholder="Building, Street, Area" className="ci-input" />
          </div>

          <div className="ci-field">
            <label className="ci-label">City</label>
            <input name="city" value={org.city || ""} onChange={handleChange} placeholder="e.g. Noida" className="ci-input" />
          </div>

          <div className="ci-field">
            <label className="ci-label">State</label>
            <input name="state" value={org.state || ""} onChange={handleChange} placeholder="e.g. Uttar Pradesh" className="ci-input" />
          </div>

          <div className="ci-field">
            <label className="ci-label">Pincode</label>
            <input name="pincode" value={org.pincode || ""} onChange={handleChange} placeholder="201301" className="ci-input" />
          </div>

          <div className="ci-field">
            <label className="ci-label">Country</label>
            <select name="country" value={org.country || "India"} onChange={handleChange} className="ci-select">
              <option>India</option>
              <option>USA</option>
              <option>UAE</option>
            </select>
          </div>
        </div>

        <div className="ci-nav-row" style={{ marginTop: 32 }}>
           <div />
           <button className="ci-btn-primary" onClick={saveOrg} disabled={saving}>
             {saving ? 'Saving...' : <><i className="fa-solid fa-floppy-disk" style={{ marginRight: 8 }} /> Save Details</>}
           </button>
        </div>
      </div>

      <div className="ci-card" style={{ maxWidth: "900px", marginTop: 24 }}>
        <h3 className="ci-card-title">Integrations & Webhook Endpoints</h3>
        <p className="ci-card-sub">Configure external API keys and view target webhook endpoints to sync leads automatically.</p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginTop: '20px' }}>
          {/* Callyzer Section */}
          <div style={{ padding: '16px', background: '#fafafa', borderRadius: '12px', border: '1px solid #eee' }}>
            <h4 style={{ margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: '8px', color: '#111827', fontSize: '14.5px', fontWeight: 'bold' }}>
              <i className="fa-solid fa-phone" style={{ color: '#799F0C' }} /> Callyzer Integration (Calling Webhook)
            </h4>
            <div className="ci-form-grid2" style={{ gap: '16px' }}>
              <div className="ci-field">
                <label className="ci-label">Callyzer API Key</label>
                <input 
                  name="callyzer_api_key" 
                  value={org.callyzer_api_key || ""} 
                  onChange={handleChange} 
                  placeholder="Enter API Key from Callyzer Portal" 
                  className="ci-input" 
                />
              </div>
              <div className="ci-field">
                <label className="ci-label">Callyzer Webhook Secret</label>
                <input 
                  name="callyzer_webhook_secret" 
                  value={org.callyzer_webhook_secret || ""} 
                  onChange={handleChange} 
                  placeholder="Secret key for verification" 
                  className="ci-input" 
                />
              </div>
              <div className="ci-field span2">
                <label className="ci-label">Webhook URL (Configure inside Callyzer Portal)</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input 
                    readOnly 
                    value={`${API_BASE_URL}calls/webhook/`} 
                    className="ci-input" 
                    style={{ background: '#f3f4f6', flex: 1 }} 
                  />
                  <button 
                    type="button" 
                    onClick={() => copyToClipboard(`${API_BASE_URL}calls/webhook/`)} 
                    className="ci-btn-primary" 
                    style={{ padding: '0 16px', height: '40px', background: '#fff', border: '1.5px solid #d1d5db', color: '#374151' }}
                  >
                    <i className="fa-regular fa-copy" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Meta Lead Ads Webhook */}
          <div style={{ padding: '16px', background: '#fafafa', borderRadius: '12px', border: '1px solid #eee' }}>
            <h4 style={{ margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: '8px', color: '#111827', fontSize: '14.5px', fontWeight: 'bold' }}>
              <i className="fa-brands fa-facebook" style={{ color: '#1877F2' }} /> Meta Ads Lead Webhook
            </h4>
            <div className="ci-form-grid2" style={{ gap: '16px' }}>
              <div className="ci-field">
                <label className="ci-label">Verify Token (Read-only from backend .env)</label>
                <input 
                  readOnly 
                  value={metaVerifyToken || "Not configured"} 
                  className="ci-input" 
                  style={{ background: '#f0f0f0' }} 
                />
              </div>
              <div className="ci-field">
                <label className="ci-label">Status</label>
                <div className="ci-input" style={{ background: '#f0f0f0', display: 'flex', alignItems: 'center', gap: '6px', color: metaVerifyToken ? '#16a34a' : '#dc2626', fontWeight: 'bold' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: metaVerifyToken ? '#16a34a' : '#dc2626' }} />
                  {metaVerifyToken ? 'Configured & Active' : 'Setup META_WEBHOOK_VERIFY_TOKEN in .env'}
                </div>
              </div>
              <div className="ci-field span2">
                <label className="ci-label">Webhook URL (Configure inside Facebook Developers App Portal)</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input 
                    readOnly 
                    value={`${API_BASE_URL}meta-ads/webhook/`} 
                    className="ci-input" 
                    style={{ background: '#f3f4f6', flex: 1 }} 
                  />
                  <button 
                    type="button" 
                    onClick={() => copyToClipboard(`${API_BASE_URL}meta-ads/webhook/`)} 
                    className="ci-btn-primary" 
                    style={{ padding: '0 16px', height: '40px', background: '#fff', border: '1.5px solid #d1d5db', color: '#374151' }}
                  >
                    <i className="fa-regular fa-copy" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Lead Inbound Webhook (Lead Ingestion) */}
          <div style={{ padding: '16px', background: '#fafafa', borderRadius: '12px', border: '1px solid #eee' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px', color: '#111827', fontSize: '14.5px', fontWeight: 'bold' }}>
                <i className="fa-solid fa-link" style={{ color: '#799F0C' }} /> Lead Inbound Webhook (Lead Ingestion)
              </h4>
              {defaultWebhook && (
                <button 
                  type="button"
                  onClick={handleRegenerateWebhookToken}
                  style={{
                    background: '#799F0C', color: '#fff', border: 'none', 
                    padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', 
                    fontSize: '11px', fontWeight: 'bold'
                  }}
                >
                  <i className="fa-solid fa-arrows-rotate" style={{ marginRight: '4px' }} /> Regenerate Token
                </button>
              )}
            </div>
            <p className="ci-card-sub" style={{ margin: '0 0 16px 0', fontSize: '12.5px' }}>Provide this webhook URL and token to external organizations or Meta Ad Accounts to automatically receive lead data.</p>
            {defaultWebhook ? (
              (() => {
                const cleanApiBase = API_BASE_URL.startsWith('/') 
                  ? `${window.location.origin}${API_BASE_URL}` 
                  : API_BASE_URL;
                const ingestUrl = `${cleanApiBase}crm/webhooks/ingest/${defaultWebhook.token}/`;
                return (
                  <>
                    <div className="ci-form-grid2" style={{ gap: '16px' }}>
                      <div className="ci-field span2">
                        <label className="ci-label">Webhook Ingestion URL</label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <input 
                            readOnly 
                            value={ingestUrl} 
                            className="ci-input" 
                            style={{ background: '#f3f4f6', flex: 1, fontFamily: 'monospace', fontSize: '12.5px' }} 
                          />
                          <button 
                            type="button" 
                            onClick={() => {
                              navigator.clipboard.writeText(ingestUrl);
                              toast.success("Webhook URL copied!");
                            }} 
                            className="ci-btn-primary" 
                            style={{ padding: '0 16px', height: '40px', background: '#fff', border: '1.5px solid #d1d5db', color: '#374151' }}
                          >
                            <i className="fa-regular fa-copy" />
                          </button>
                        </div>
                      </div>
                      <div className="ci-field span2">
                        <label className="ci-label">Verification Token</label>
                        <input 
                          readOnly 
                          value={defaultWebhook.token} 
                          className="ci-input" 
                          style={{ background: '#f0f0f0', fontFamily: 'monospace' }} 
                        />
                      </div>
                    </div>

                    {/* Webhook Lead Buffer Section */}
                    <div style={{ marginTop: '24px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 'bold', color: '#111827' }}>
                          <i className="fa-solid fa-list-check" style={{ marginRight: 8, color: '#799F0C' }} />
                          Webhook Buffer (Potential Duplicates pending approval)
                        </h4>
                        <button 
                          type="button"
                          onClick={fetchBufferLeads} 
                          style={{
                            background: 'none', border: '1px solid #d1d5db', color: '#4b5563',
                            padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px'
                          }}
                        >
                          <i className="fa-solid fa-arrows-rotate" style={{ marginRight: 4 }} /> Refresh
                        </button>
                      </div>

                      {bufferLoading ? (
                        <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
                          <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: 8 }} /> Loading duplicate buffer...
                        </div>
                      ) : bufferLeads.length === 0 ? (
                        <div style={{ padding: '16px', textAlign: 'center', color: '#999', fontSize: '13px', background: '#fafafa', borderRadius: '8px', border: '1px dashed #ddd' }}>
                          No pending duplicate webhook leads in staging. All inbound leads have been successfully routed!
                        </div>
                      ) : (
                        <div className="table-responsive" style={{ maxHeight: '350px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '8px' }}>
                          <table className="ci-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead>
                              <tr style={{ background: '#fafafa', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                                <th style={{ padding: '10px' }}>Name</th>
                                <th style={{ padding: '10px' }}>Contact Info</th>
                                <th style={{ padding: '10px' }}>Course / Source</th>
                                <th style={{ padding: '10px' }}>Notes</th>
                                <th style={{ padding: '10px', textAlign: 'right' }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bufferLeads.map((lead) => (
                                <tr key={lead.id} style={{ borderBottom: '1px solid #eee' }}>
                                  <td style={{ padding: '10px', fontWeight: '600' }}>{lead.first_name} {lead.last_name}</td>
                                  <td style={{ padding: '10px' }}>
                                    <div>📞 {lead.phone}</div>
                                    {lead.email && <div style={{ fontSize: '11.5px', color: '#666' }}>✉️ {lead.email}</div>}
                                  </td>
                                  <td style={{ padding: '10px' }}>
                                    <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 6px', borderRadius: '4px', fontSize: '11.5px', fontWeight: '600' }}>
                                      {lead.course || 'Webhook Lead'}
                                    </span>
                                    <div style={{ fontSize: '11px', color: '#999', marginTop: '3px' }}>Src: {lead.external_source || 'Webhook'}</div>
                                  </td>
                                  <td style={{ padding: '10px', color: '#4b5563', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={lead.notes}>
                                    {lead.notes}
                                  </td>
                                  <td style={{ padding: '10px', textAlign: 'right', display: 'flex', gap: '6px', justifyContent: 'flex-end', alignItems: 'center' }}>
                                    <button
                                      type="button"
                                      onClick={() => handleTransfer(lead.id, 'merge')}
                                      style={{ padding: '4px 8px', fontSize: '11px', background: '#799F0C', border: 'none', color: '#fff', cursor: 'pointer', borderRadius: '4px', fontWeight: 'bold' }}
                                      title="Merge notes and details into the existing duplicate lead"
                                    >
                                      Merge
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleTransfer(lead.id, 'create')}
                                      style={{ padding: '4px 8px', fontSize: '11px', border: '1px solid #d1d5db', background: '#fff', color: '#374151', borderRadius: '4px', cursor: 'pointer' }}
                                      title="Force create a brand new lead card ignoring duplicate warnings"
                                    >
                                      Force Create
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setEditLead(lead)}
                                      style={{ padding: '4px 6px', fontSize: '11.5px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: '4px', cursor: 'pointer' }}
                                      title="Edit contact info or details before approving"
                                    >
                                      <i className="fa-solid fa-pen-to-square" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleIgnore(lead.id)}
                                      style={{ padding: '4px 6px', fontSize: '11.5px', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '4px', cursor: 'pointer' }}
                                      title="Ignore and discard this duplicate lead"
                                    >
                                      <i className="fa-solid fa-trash" />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </>
                );
              })()
            ) : (
              <div style={{ textAlign: 'center', padding: '16px', background: '#f9f9f9', borderRadius: '8px', color: '#666' }}>
                Loading webhook settings...
              </div>
            )}
          </div>
        </div>

        <div className="ci-nav-row" style={{ marginTop: 24 }}>
          <div />
          <button className="ci-btn-primary" onClick={saveOrg} disabled={saving}>
            {saving ? 'Saving...' : <><i className="fa-solid fa-floppy-disk" style={{ marginRight: 8 }} /> Save Keys & Details</>}
          </button>
        </div>
      </div>
      {/* QUICK EDIT WEBHOOK BUFFER MODAL */}
      {editLead && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)',
          display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div style={{
            background: '#ffffff', padding: '24px', borderRadius: '12px',
            width: '100%', maxWidth: '500px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '800', color: '#1e293b' }}>
              📝 Edit Buffered Lead Details
            </h3>
            <form onSubmit={handleSaveEdit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569' }}>First Name</label>
                  <input
                    type="text"
                    value={editLead.first_name || ''}
                    onChange={e => setEditLead({ ...editLead, first_name: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', marginTop: '4px' }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569' }}>Last Name</label>
                  <input
                    type="text"
                    value={editLead.last_name || ''}
                    onChange={e => setEditLead({ ...editLead, last_name: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', marginTop: '4px' }}
                  />
                </div>
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569' }}>Phone Number</label>
                <input
                  type="text"
                  value={editLead.phone || ''}
                  onChange={e => setEditLead({ ...editLead, phone: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', marginTop: '4px' }}
                  required
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569' }}>Email Address</label>
                <input
                  type="email"
                  value={editLead.email || ''}
                  onChange={e => setEditLead({ ...editLead, email: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', marginTop: '4px' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569' }}>Course / Program</label>
                <input
                  type="text"
                  value={editLead.course || ''}
                  onChange={e => setEditLead({ ...editLead, course: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', marginTop: '4px' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569' }}>Notes</label>
                <textarea
                  value={editLead.notes || ''}
                  onChange={e => setEditLead({ ...editLead, notes: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', marginTop: '4px', minHeight: '80px', fontFamily: 'inherit' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '12px' }}>
                <button
                  type="button"
                  onClick={() => setEditLead(null)}
                  style={{ padding: '8px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ padding: '8px 24px', background: '#799F0C', color: '#ffffff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '700' }}
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

